import { useActorMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ContentProgressPageLayout } from '@/layout/components/content-progress-page-layout/ContentProgressPageLayout'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { useSignableDocuments } from '../../hooks/useSignableDocuments'
import { SignableFormActivity } from './SignableFormActivity'
import { SignableFormActivityReview } from './SignableFormReview'

import type { ContentProgressPageLayoutHandle } from '@/layout/components/content-progress-page-layout/ContentProgressPageLayout'
import type { SignableDocumentDetailActivity } from '../../types/documents.types'

const SIGNING_STEPS = ['', 'form', 'review']

const DocumentSignFlow = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const actorMember = useActorMember()
  const pageLayoutRef = useRef<ContentProgressPageLayoutHandle>()

  const isLoggedInForMandatee = useLoggedInForMandatee()
  useHideNativeNavigation()

  // Component State
  // ------------------------------------------------------------------------- /
  const [index, setIndex] = useState<number>(1)

  const {
    getSignableDocumentDetails: {
      execute: getSignableDocumentDetails,
      value: signableDocumentDetails,
      isLoading: isLoadingSignableDocumentDetails,
    },
    updateSignableDocumentActivity: {
      execute: updateSignableDocumentActivity,
      // isLoading: isUpdatingSignableDocumentActivity,
    },
  } = useSignableDocuments()

  // Functions
  // ------------------------------------------------------------------------- /
  function handleGoBack() {
    mixpanel.track('document_signing_cancel', {
      step: SIGNING_STEPS[index],
      is_mandate: isLoggedInForMandatee,
    })
    // navigate(`../../documents/personal/${id}`, { relative: 'path' })
    navigate(-1)
  }

  async function onFormComplete(formValues: object) {
    const mappedFormValues = Object.entries(formValues).map(([key, value]) => {
      return { fieldId: Number.parseInt(key), value }
    })

    const promises = activities
      ?.filter((activity) => activity.definition.type === 'Form')
      ?.map((activity) => {
        const inputs = [...activity.inputs, ...mappedFormValues]

        const signatureInput = activity.definition?.fields?.find((field) => field.name === 'signature')
        if (signatureInput) {
          inputs.push({
            fieldId: signatureInput.id,
            value: `${actorMember?.name} \n
            ${actorMember?.nationalId} \n
            ${formatLocale(new Date(), 'dd-MM-yyyy')} `,
          })
        }

        const signDate = activity.definition?.fields?.find((field) => field.name === 'sign_date')
        if (signDate) {
          inputs.push({
            fieldId: signDate.id,
            value: formatLocale(new Date(), 'dd-MM-yyyy'),
          })
        }

        return updateSignableDocumentActivity(activity?.id, {
          inputs,
        })
      })
    await Promise.all(promises)

    pageLayoutRef.current?.handleNext()
  }

  const forceReload = useCallback(() => {
    if (id) {
      getSignableDocumentDetails(Number.parseInt(id, 10))
    }
  }, [id])

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (id) {
      getSignableDocumentDetails(Number.parseInt(id, 10))
    }
  }, [id])

  useEffect(() => {
    if (index !== 0) {
      forceReload()
    }
  }, [forceReload, index])

  // Template variables
  // ------------------------------------------------------------------------- /
  const activities: SignableDocumentDetailActivity[] = useMemo(() => {
    return signableDocumentDetails?.activities || []
  }, [signableDocumentDetails])

  const formFields = useMemo(() => {
    return activities
      .filter((activity) => activity.definition.type === 'Form')
      .reduce(
        (previous, current) => {
          // dedupe the field name
          const currentFields = current.definition.fields || []
          // Enrich the field with an overall order so we can sort them flat in the form render
          // Messages use their activity definition and fields need to also take account their form definition
          // to maintain order
          const enrichedFields = currentFields.map((field) => ({
            ...field,
            overallOrder: current.definition.order + field.order / 100,
          }))
          return [...previous, ...enrichedFields]
        },
        [] as SignableDocumentDetailActivity['definition']['fields'],
      )
  }, [activities])

  const formInputs = useMemo(() => {
    return activities
      .filter((activity) => activity.definition.type === 'Form')
      .reduce(
        (previous, current) => {
          // dedupe the field name
          const currentInputs = current.inputs || []
          return [...previous, ...currentInputs]
        },
        [] as SignableDocumentDetailActivity['inputs'],
      )
  }, [activities])

  const messageActivities = useMemo(() => {
    return activities
      .filter((activity) => activity.definition.type === 'Message')
      .map((activity) => ({
        ...activity,
        overallOrder: activity.definition.order,
      }))
      .slice(1)
  }, [activities])

  // Render
  // ------------------------------------------------------------------------- /
  if (isLoadingSignableDocumentDetails) {
    return <AnimatedLoader key={'loader'} />
  }

  return (
    <ContentProgressPageLayout
      headerPrimaryAction={{
        icon: ['far', 'chevron-left'],
        label: t({
          id: 'globals.previous-step',
          comment: 'Global for previous step',
          message: 'Vorige stap',
        }),
      }}
      headerSecondaryAction={{
        icon: ['far', 'close'],
        label: t({
          id: 'globals.cancel',
          comment: 'Global for cancel',
          message: 'Annuleer',
        }),
      }}
      index={index}
      onUpdateIndex={setIndex}
      onGoBack={handleGoBack}
      maxIndex={2}
      minIndex={0}
      ref={pageLayoutRef}
    >
      {index === 1 ? (
        <SignableFormActivity
          inputs={formInputs}
          fields={formFields}
          messages={messageActivities}
          numDocuments={activities?.filter((x) => x.definition.type === 'Document').length || 0}
          onNext={onFormComplete}
        />
      ) : index === 2 ? (
        <SignableFormActivityReview
          details={signableDocumentDetails}
          onSigned={() => navigate(-1)}
          onAcceptDocument={forceReload}
        />
      ) : null}
    </ContentProgressPageLayout>
  )
}

export { DocumentSignFlow }
